import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { paymentKeys } from '@/hooks/usePayments'
import { parkingSessionKeys } from '@/hooks/useParkingSessions'
import type { ApiResponse } from '@/types'
import type { LprEvent, LprVerificationStatus } from '@/types/parking'

export const lprEventKeys = {
  all: ['lpr_events'] as const,
  lists: () => [...lprEventKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...lprEventKeys.lists(), filters] as const,
}

export interface LprEventFilters {
  page?: number
  property_id?: number
  vehicle_id?: number
  /** Array of vehicle ids — sent as vehicle_id[] query params for a WHERE IN filter. */
  vehicle_ids?: number[]
  /** Maximum number of results to return (sent as per_page). */
  limit?: number
  verification_status?: LprVerificationStatus | ''
  captured_at_from?: string
  captured_at_to?: string
  /** EP-106: partial plate-text search (ILIKE on server). */
  plate_text?: string
  /** EP-106: exact vehicle type filter (data-driven from champion classifier). */
  vehicle_type?: string
  /** EP-106: exact color filter (data-driven from champion classifier). */
  color?: string
  /** EP-106: exact direction filter (raw ml domain: in|out|parked|unclear). */
  direction?: string
  /** When false, the query will not execute. Defaults to true. */
  enabled?: boolean
}

export interface LprAttributeFacets {
  vehicle_types: string[]
  colors: string[]
  directions: string[]
}

export function useLprEvents(filters: LprEventFilters = {}) {
  const { enabled = true, ...queryFilters } = filters
  return useQuery({
    queryKey: lprEventKeys.list(queryFilters as Record<string, unknown>),
    enabled,
    queryFn: async () => {
      const params: Record<string, unknown> = {}
      if (queryFilters.page) params.page = queryFilters.page
      if (queryFilters.property_id) params.property_id = queryFilters.property_id
      if (queryFilters.vehicle_id) params.vehicle_id = queryFilters.vehicle_id
      // vehicle_ids array: send as vehicle_id[] so Rails WHERE IN applies
      if (queryFilters.vehicle_ids && queryFilters.vehicle_ids.length > 0) {
        params['vehicle_id[]'] = queryFilters.vehicle_ids
      }
      if (queryFilters.limit) params.per_page = queryFilters.limit
      if (queryFilters.verification_status) params.verification_status = queryFilters.verification_status
      if (queryFilters.captured_at_from) params.captured_at_from = queryFilters.captured_at_from
      if (queryFilters.captured_at_to) params.captured_at_to = queryFilters.captured_at_to
      // EP-106: attribute filters — only forwarded when non-empty
      if (queryFilters.plate_text) params.plate_text = queryFilters.plate_text
      if (queryFilters.vehicle_type) params.vehicle_type = queryFilters.vehicle_type
      if (queryFilters.color) params.color = queryFilters.color
      if (queryFilters.direction) params.direction = queryFilters.direction
      const { data } = await api.get<ApiResponse<LprEvent[]>>(
        '/api/lpr_events',
        { params }
      )
      return data
    },
    staleTime: 10_000,
    refetchInterval: 10_000, // 10s auto-refresh for near-real-time
  })
}

/**
 * EP-106: Fetches attribute facets (distinct non-null vehicle_types / colors /
 * directions) for the given property. The facets are used to populate the
 * attribute filter dropdowns in LprEventLogPage. Scoped to the current account
 * by the backend policy; optionally further narrowed to a single property.
 */
export function useLprAttributeFacets(propertyId?: number) {
  return useQuery<LprAttributeFacets>({
    queryKey: ['lpr_events', 'attribute_facets', propertyId],
    queryFn: async () => {
      const params: Record<string, unknown> = {}
      if (propertyId) params.property_id = propertyId
      const { data } = await api.get<LprAttributeFacets>(
        '/api/lpr_events/attribute_facets',
        { params }
      )
      return data
    },
    staleTime: 60_000,
  })
}

// T148A-007 (EP-148 Amendment A, ruling i, frontend #2): operator "Settle &
// check out" action on an unpaid-candidate LPR row. POSTs to the
// ParkingSessionSettlement endpoint T148A-006 built, NOT
// /api/external_parking_sessions/:id/settle, per the architect's required
// rename. On success, invalidate every lpr_events list query so the row's
// unpaid_candidate_paid flips live on the next refetch (no new LprEvent
// write path; the badge is already a live derivation).
//
// EP-161 (T161-R03b, T161-W02a): the same settlement is the gate's "รับเงินสด"
// on ที่จอดรถวันนี้. Cash needs cash_photo_key (the photo of the money,
// presigned on the session); the amount may be left out when the session
// already has a payment, and the server then takes its charge. A settlement
// also changes the session's state, the payments lists and the dashboard.
export interface SettleParkingSessionInput {
  parking_session_id: number
  /** Required only when the session has no payment yet (the overstay case). */
  amount?: number
  payment_method: 'cash' | 'transfer'
  /** Required for cash. */
  cash_photo_key?: string
}

export interface SettleParkingSessionResult {
  session_id: number
  payment_id: number
  verification_status: string
  exited_at: string | null
}

export function useSettleParkingSession() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: SettleParkingSessionInput) => {
      const { data } = await api.post<ApiResponse<SettleParkingSessionResult>>(
        '/api/parking_session_settlements',
        input,
      )
      return data.data
    },
    // Both callers show the refusal inline, mapped from its code.
    meta: { suppressErrorToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: lprEventKeys.lists() })
      queryClient.invalidateQueries({ queryKey: parkingSessionKeys.all })
      queryClient.invalidateQueries({ queryKey: paymentKeys.all })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}
